import React, { useMemo } from "react";
import {
  GLOBAL_SEARCH_QUERY_PARAM_KEY,
  SEARCH_RESULT_TAB_QUERY_PARAM_KEY,
  GLOBAL_SEARCH_RESULTS_SECTIONS,
  defaultSlidesPerView,
} from "@/utils/constant";
import { Box } from "@dxp-feed/ui/Box";
import { Text } from "@dxp-feed/ui/Text";
import Link from "@/components/Link";
import ArticleCard from "@/templates/articles/_components/ArticleCard";
import OfferCard from "@/components/OfferCard";
import { RecipeCard } from "@/templates/recipes/_components/RecipeCard";
import EmblaSlider from "@/components/EmblaSlider";
import { useSelector } from "react-redux";
import { RootState } from "@/utils/redux/store";
import { GlobalSearchSectionsType } from "@/utils/types/algolia";
import { useAlgoliaViewAnalyticsEvent } from "@/utils/hooks/useAlgoliaViewAnalyticsEvent";
import { Separator } from "@dxp-feed/ui";
import { usePathname } from "next/navigation";

interface BaseSliderProps {
  type: GlobalSearchSectionsType;
  slidesPerView?: {
    base: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
    xxl: number;
  };
  showControls?: boolean;
  isGlobalSearch?: boolean;
  isSearchPage?: boolean;
  slideCount?: number | null;
}

// Extends BaseSliderProps and adds unique props for PopularSliderSection
interface PopularSliderSectionProps extends BaseSliderProps {
  setDropDown?: (value: boolean) => void; // Avoid `any` for better type safety
  title: string;
  titleCTA?: string;
}

// Extends BaseSliderProps and adds unique props for PopularDataSlider
interface PopularDataSliderProps extends BaseSliderProps {
  className?: string;
}

export default function PopularSliderSection({
  isGlobalSearch = false,
  type,
  setDropDown,
  title,
  titleCTA,
  showControls,
  slidesPerView,
  slideCount,
  isSearchPage,
}: Readonly<PopularSliderSectionProps>) {
  const pathname = usePathname();
  const selectedTab =
    GLOBAL_SEARCH_RESULTS_SECTIONS[
      type
        .toLocaleUpperCase()
        .replace(/S$/, "") as keyof typeof GLOBAL_SEARCH_RESULTS_SECTIONS
    ];

  const popularItems = useSelector(
    (state: RootState) => state.algoliaSlice.popularItems
  );
  return (
    <>
      {!isGlobalSearch && pathname !== "/" && (
        <Separator
          orientation="horizontal"
          color="gray"
          className="border-grey350 !h-0 lg:!h-[1px] border-b-0 lg:border-b-1 mt-0 lg:mt-6"
        />
      )}
      {popularItems?.[type]?.nbHits ? (
        <>
          <Box as="div" className="pt-4 heading-wrapper lg:pt-8 ">
            <Box as="div" className=" flex justify-between items-center">
              <Text
                as="h2"
                className={`heading text-left w-full flex-1 font-sans font-extraBold ${isGlobalSearch ? "text-base" : "text-md xs:text-2xl"}`}
              >
                {title}
              </Text>
              <Link
                href={`/?${GLOBAL_SEARCH_QUERY_PARAM_KEY}=${encodeURIComponent("")}&${SEARCH_RESULT_TAB_QUERY_PARAM_KEY}=${selectedTab}`}
                aria-label={`Click to ${titleCTA} ${type}`}
                className={`inline-block ${isGlobalSearch ?'text-sm 2xs:text-body':'text-[14px]'} py-[10px] px-[12px] font-bold text-brandSecondary`}
                onClick={() => setDropDown && setDropDown(false)}
              >
                <Text>{titleCTA}</Text>
              </Link>
            </Box>
          </Box>
          <Box
            as="div"
            className={`${isGlobalSearch ? "!pt-2" : "pt-3 md:pt-4 2md:pt-[24px]"} slider-wrapper lg:mr-0`}
          >
            <PopularDataSlider
              showControls={showControls}
              type={type}
              slidesPerView={slidesPerView}
              isGlobalSearch={isGlobalSearch}
              slideCount={slideCount}
              isSearchPage={isSearchPage}
            />
          </Box>
        </>
      ) : null}
    </>
  );
}

interface PopularDataSliderProps {
  type: GlobalSearchSectionsType;
  className?: string;
  slideCount?: number | null;
  slidesPerView?: {
    base: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
    xxl: number;
  };
  showControls?: boolean;
  isGlobalSearch?: boolean;
}

export const PopularDataSlider = ({
  className,
  type,
  slideCount,
  slidesPerView,
  showControls = true,
  isGlobalSearch = false,
  isSearchPage = false,
}: PopularDataSliderProps) => {
  const popularItems = useSelector(
    (state: RootState) => state.algoliaSlice.popularItems
  );

  const gridRef = useAlgoliaViewAnalyticsEvent(
    popularItems?.[type]?.hits || [],
    type
  );

  const finalSlidesPerView = useMemo(() => {
    return slidesPerView || defaultSlidesPerView[type];
  }, [slidesPerView, type]);
  const mappedData = useMemo(() => {
    if (!popularItems?.products) return null;

    switch (type) {
      case "products": {
        const data = slideCount
          ? popularItems.products.hits.slice(0, slideCount)
          : popularItems.products.hits;
        return data.map((item) => (
          <React.Fragment key={item.objectID}>
            {isGlobalSearch ? (
              <OfferCard
                item={item}
                showAddToCartButton={false}
                showFavoriteButton={false}
                isGlobalSearch={isGlobalSearch}
              />
            ) : (
              <OfferCard item={item} />
            )}
          </React.Fragment>
        ));
      }
      case "articles": {
        const data = slideCount
          ? popularItems.articles.hits.slice(0, slideCount)
          : popularItems.articles.hits;

        return data?.map((item) => (
          <ArticleCard key={item.objectID} data={item} />
        ));
      }
      case "recipes": {
        const data = slideCount
          ? popularItems.recipes.hits.slice(0, slideCount)
          : popularItems.recipes.hits;
        return data?.map((item) => (
          <RecipeCard
            key={item.objectID}
            item={item}
            list_name="Featured Recipes"
          />
        ));
      }
      default:
        return null;
    }
  }, [type, popularItems]);

  return (
    <Box as="div" className={`${className}`}>
      <EmblaSlider
        ref={gridRef}
        align="start"
        options={{ loop: true, align: "start", dragFree: true }}
        slidesPerView={finalSlidesPerView}
        slidesToScroll={finalSlidesPerView}
        showControls={showControls}
        thumbnails={false}
        // type={type}
        className={`${isGlobalSearch || isSearchPage ? "globalSearchslider" : "roasterSlider"} [&_h3]:line-clamp-2 ${type}-slider`}
      >
        {mappedData}
      </EmblaSlider>
    </Box>
  );
};
